import React from 'react';
import {
  Button,
  DropDown,
  Flex,
  FlexColumn,
  FormField,
  PasswordField,
  TextField,
  formFieldRequiredLabel as requiredLabel,
  IconButton,
} from '@elementary/common-ui/components';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus-black.svg';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import FormControls from '@elementary/common-ui/components/FormControls';
import { Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DatadogActivityLogsIntegrationFormValues } from './activityLogsIntegrationUtils';
const DATADOG_SITE_OPTIONS = [
  { value: 'datadoghq.com', label: 'datadoghq.com' },
  { value: 'datadoghq.eu', label: 'datadoghq.eu' },
  { value: 'us3.datadoghq.com', label: 'us3.datadoghq.com' },
  { value: 'us5.datadoghq.com', label: 'us5.datadoghq.com' },
  { value: 'ap1.datadoghq.com', label: 'ap1.datadoghq.com' },
  { value: 'ap2.datadoghq.com', label: 'ap2.datadoghq.com' },
  { value: 'ddog-gov.com', label: 'ddog-gov.com' },
];

const DatadogActivityLogsForm = () => {
  const methods = useFormContext<DatadogActivityLogsIntegrationFormValues>();
  const {
    register,
    control,
    setValue,
    formState: { dirtyFields, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'properties.tags',
  });

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <FormField title="Connection name" errorMessage={errors.name?.message}>
        <TextField
          {...register('name', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="Connection name"
        />
      </FormField>

      <FormField
        title="Datadog API key"
        errorMessage={errors.properties?.api_key?.message as string}
      >
        <PasswordField
          {...register('properties.api_key', { required: requiredLabel })}
          size="large"
          isFormValueDirty={dirtyFields.properties?.api_key}
          clearFormValue={() => setValue('properties.api_key', '')}
          placeholder="Paste your Datadog API key"
        />
      </FormField>

      <FormField
        title="Datadog site"
        errorMessage={errors.properties?.site?.message as string}
      >
        <Controller
          control={control}
          name="properties.site"
          rules={{ required: requiredLabel }}
          render={({ field }) => (
            <DropDown
              {...field}
              options={DATADOG_SITE_OPTIONS}
              placeholder="Select a Datadog site"
              size="large"
            />
          )}
        />
      </FormField>

      <FormField
        title="Source"
        errorMessage={errors.properties?.source?.message as string}
      >
        <TextField
          {...register('properties.source', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="e.g. elementary"
        />
      </FormField>

      <FormField
        title="Service"
        errorMessage={errors.properties?.service?.message as string}
      >
        <TextField
          {...register('properties.service', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="e.g. elementary-app"
        />
      </FormField>

      <FormField title="Tags" optional>
        <FlexColumn sx={{ gap: 1 }}>
          {fields.map((tag, index) => (
            <Flex gap={1} alignItems="center" key={tag.id}>
              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="large"
                  placeholder="key"
                  {...register(`properties.tags.${index}.key`, {
                    required: requiredLabel,
                  })}
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.properties?.tags?.[index]?.key?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="large"
                  placeholder="value"
                  {...register(`properties.tags.${index}.value`, {
                    required: requiredLabel,
                  })}
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.properties?.tags?.[index]?.value?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <IconButton>
                <TrashIcon onClick={() => remove(index)} />
              </IconButton>
            </Flex>
          ))}

          <Button
            startIcon={<PlusIcon />}
            onClick={() => append({ key: '', value: '' })}
            sx={{ width: '120px' }}
            variant="text"
            color="secondary"
          >
            <Typography fontSize={14}>Add tag</Typography>
          </Button>
        </FlexColumn>
      </FormField>
    </FlexColumn>
  );
};

export default DatadogActivityLogsForm;
